<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO } from 'date-fns';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Each entry is a post record enriched by PostsIndex with `isScheduled` and
// `pageName` -- this component stays a plain, stateless row renderer for
// whatever mixed (published + scheduled) list the active filter pill leaves
// it holding.
const props = defineProps({
  posts: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  selectedId: { type: Number, default: null },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const isEmpty = computed(() => !props.isLoading && props.posts.length === 0);

// postedAt doubles as the scheduled time for a scheduled post, so the same
// relative-time formatting works for both: date-fns signs the distance
// ("in 3 days" for a future timestamp, "3 days ago" for a past one).
const relativePostedAt = postedAt => {
  if (!postedAt) return t('COMMENTS.LIST.NO_DATE');
  return formatDistanceToNow(parseISO(postedAt), { addSuffix: true });
};

// Purely derived from data we actually have -- a real image posts as
// `pictureUrl`, so there is no field to fabricate here, only a choice of
// icon for what is already known.
const typeIcon = post =>
  post.pictureUrl ? 'i-lucide-image' : 'i-lucide-file-text';

const initials = name => (name || '?').trim().charAt(0).toUpperCase();
</script>

<template>
  <div class="flex flex-col w-full h-full gap-1 overflow-y-auto">
    <template v-if="isLoading">
      <div
        v-for="skeletonRow in 4"
        :key="skeletonRow"
        data-testid="post-row-skeleton"
        class="flex flex-col w-full gap-2 px-[14px] py-3 rounded-[22px] animate-pulse"
      >
        <div class="w-2/3 h-3 rounded bg-n-solid-2" />
        <div class="w-full h-3 rounded bg-n-solid-2" />
        <div class="w-1/3 h-2.5 rounded bg-n-solid-2" />
      </div>
    </template>
    <p
      v-else-if="isEmpty"
      data-testid="posts-empty-state"
      class="px-2 py-4 text-sm text-n-slate-10"
    >
      {{ t('COMMENTS.POSTS.LIST_EMPTY') }}
    </p>
    <template v-else>
      <button
        v-for="post in posts"
        :key="post.id"
        type="button"
        data-testid="post-row"
        class="flex flex-col items-start w-full gap-[7px] px-[14px] py-3 text-left rounded-[22px] hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:-outline-offset-2"
        :class="{ 'bg-n-alpha-2': selectedId === post.id }"
        @click="emit('select', post.id)"
      >
        <div class="flex items-center w-full gap-2">
          <span
            class="grid text-[11px] font-semibold rounded-full size-[26px] shrink-0 bg-n-solid-2 text-n-slate-11 place-items-center"
          >
            {{ initials(post.pageName) }}
          </span>
          <span class="flex-1 text-[12.5px] font-bold truncate text-n-slate-12">
            {{ post.pageName }}
          </span>
          <span
            data-testid="post-row-status"
            class="px-[9px] py-px text-[9.5px] font-medium uppercase tracking-wide rounded-full shrink-0"
            :class="
              post.isScheduled
                ? 'bg-n-amber-3 text-n-amber-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            {{
              post.isScheduled
                ? t('COMMENTS.POSTS.FILTERS.SCHEDULED')
                : t('COMMENTS.POSTS.FILTERS.PUBLISHED')
            }}
          </span>
        </div>
        <p
          class="w-full text-[13.5px] leading-[1.45] line-clamp-2 text-n-slate-12"
        >
          {{ post.message || t('COMMENTS.POSTS.NO_MESSAGE') }}
        </p>
        <div
          class="flex items-center w-full gap-1.5 text-[11.5px] text-n-slate-10"
        >
          <Icon :icon="typeIcon(post)" class="shrink-0 size-3" />
          <span class="flex-1 truncate" data-testid="post-time">
            {{ relativePostedAt(post.postedAt) }}
          </span>
          <span
            v-if="!post.isScheduled"
            data-testid="post-comment-count"
            class="shrink-0"
          >
            {{
              t('COMMENTS.POSTS.COMMENT_COUNT', { count: post.commentCount })
            }}
          </span>
        </div>
      </button>
    </template>
  </div>
</template>
